/* globals window navigator */

import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import {
  all,
  call,
  delay,
  put,
  takeEvery,
  takeLatest
} from 'redux-saga/effects';

import { logEvent } from 'common/helpers/analytics';
import { addAPIError, removeAPIError } from 'redux/apiErrors/sagas';
import {
  addCustomerAddress,
  editCustomerAddress
} from 'redux/customerAddresses/api';
import {
  addCustomerCreditCard,
  editCustomerCreditCard
} from 'redux/customerCreditCards/api';
import { getOrder, getOrderByEmailOrderNumber } from 'redux/orders/api';
import { GET_ORDER_SUCCESS } from 'redux/orders/constants';

import { getCustomer } from '../customer/api';

import {
  addCouponOrGiftCard,
  addItem,
  chargeAtPayGate,
  chargeCart,
  createCart,
  getCart,
  removeItem,
  replaceTender,
  sendCartToHostedPayment,
  updateCart,
  updateItem
} from './api';
import {
  ADD_CART_ITEM_REQUEST,
  ADD_CART_ITEM_SUCCESS_GTM,
  ADD_CART_QUICK_ITEM_REQUEST,
  ADD_COUPON_OR_GIFTCARD_REQUEST,
  ADD_COUPON_OR_GIFTCARD_SUCCESS_GTM,
  CHARGE_CART_REQUEST,
  CHARGE_CART_SUCCESS_GTM,
  CHECKOUT_STEP_REQUEST,
  CHECKOUT_STEP_REQUEST_GTM,
  GET_CART_HOSTED_PAYMENT_REQUEST,
  GET_CART_REQUEST,
  GET_CART_REQUESTING,
  GET_CART_SUCCESS,
  REMOVE_CART_ITEM_REQUEST,
  REMOVE_CART_ITEM_SUCCESS_GTM,
  RESET_CART_ISFETCHING,
  SEND_CART_TO_HOSTED_PAYMENT_REQUEST,
  SIDE_CART_VISIBLE,
  UPDATE_AND_CHARGE_CART_PAYGATE_REQUEST,
  UPDATE_AND_CHARGE_CART_REQUEST,
  UPDATE_CART_AND_SAVE_SHIPPING_ADDRESS_REQUEST,
  UPDATE_CART_ITEM_REQUEST,
  UPDATE_CART_REQUEST
} from './constants';

export function* get() {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    let hasCart = false;
    if (customerId()) {
      const response = yield call(getCustomer);
      const { cart } = response.data.orderInformation;
      if (cart) {
        hasCart = true;
        Cookies.set('cartId', cart.id, {
          expires: 365,
          secure: process.env.NODE_ENV === 'production'
        });
        yield put({ type: GET_CART_SUCCESS, payload: cart });
      }
    }
    if (!hasCart && cartId()) {
      const response = yield call(getCart, cartId());
      hasCart = true;
      yield put({ type: GET_CART_SUCCESS, payload: response.data });
    }
    if (!hasCart) {
      yield put({ type: GET_CART_SUCCESS, payload: {} });
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      Cookies.remove('cartId');
      yield put({ type: GET_CART_SUCCESS, payload: {} });
    } else {
      yield put({ type: RESET_CART_ISFETCHING });
      yield call(handleError, error, 'get');
    }
  }
}

export function* getForHostedPayment(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    const response = yield call(getCart, cartId());
    yield put({ type: GET_CART_SUCCESS, payload: response.data });
    const cart = response.data;
    if (cart.status === 'Completed') {
      yield put({ type: GET_CART_SUCCESS, payload: response.data });
      Cookies.remove('cartId');
      let order;
      if (Cookies.get('customerToken')) {
        order = yield call(getOrder, customerId(), response.data.orderId);
      } else {
        order = yield call(
          getOrderByEmailOrderNumber,
          response.data.email,
          response.data.orderNumber
        );
      }
      yield put({ type: GET_ORDER_SUCCESS, payload: order.data });
      yield put({ type: CHARGE_CART_SUCCESS_GTM, payload: order.data });
      yield call(recordPurchase, order.data);
      if (action.successFunction) {
        // Send the cart rather than the order
        action.successFunction(cart);
      }
    } else {
      const isWaitingTender = cart.tenders.find(
        (tender) => tender.chargeStatus === 'Waiting'
      );
      if (!isWaitingTender) {
        const err = {
          response: {
            status: 402,
            data: {
              message: 'Payment Declined'
            }
          }
        };
        action.errorFunction();
        yield call(handleError, err, 'updateAndCharge');
      }
    }
  } catch (error) {
    action.errorFunction();
    yield call(handleError, error, 'updateAndCharge');
  }
}

export function* update(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    const response = yield call(updateCart, cartId(), action.cart);
    yield put({ type: GET_CART_SUCCESS, payload: response.data });
    if (action.successFunction) {
      action.successFunction(response.data);
    }
  } catch {
    try {
      delete action.cart.metaData;
      const response = yield call(updateCart, cartId(), action.cart);
      yield put({ type: GET_CART_SUCCESS, payload: response.data });
      if (action.successFunction) {
        action.successFunction(response.data);
      }
    } catch {
      if (action.errorFunction) {
        action.errorFunction();
      }
      yield call(handleError, 'update');
    }
  }
}

export function* updateAndSaveShipping(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    const { address, cart } = action;
    cart.shipTo = yield call(saveAddress, address);
    const response = yield call(updateCart, cartId(), cart);
    yield put({ type: GET_CART_SUCCESS, payload: response.data });
    if (action.successFunction) {
      action.successFunction(response.data);
    }
  } catch (error) {
    yield call(handleError, error, 'updateAndSaveShipping');
  }
}

function* sendToHostedPayment(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    const response = yield call(sendCartToHostedPayment, cartId());
    action.redirectFunction(response.data);
  } catch (error) {
    yield call(handleError, error, 'updateAndCharge');
  }
}

export function* updateAndChargePayGate(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    const { address, creditCard } = action;
    const cart = {
      billTo: address
    };
    if (action.isSaveAddress) {
      cart.billTo = yield call(saveAddress, address);
    }
    const response = yield call(chargeAtPayGate, cartId(), creditCard);
    if (response.data.redirectRequired) {
      // Do Redirect steps
      action.redirectFunction(response.data);
    } else if (response.data.status === 'Completed') {
      // Successfully Charged
      yield put({ type: GET_CART_SUCCESS, payload: response.data });
      Cookies.remove('cartId');
      let order;
      if (Cookies.get('customerToken')) {
        order = yield call(getOrder, customerId(), response.data.orderId);
      } else {
        order = yield call(
          getOrderByEmailOrderNumber,
          response.data.email,
          response.data.orderNumber
        );
      }
      yield put({ type: GET_ORDER_SUCCESS, payload: order.data });
      yield put({ type: CHARGE_CART_SUCCESS_GTM, payload: order.data });
      yield call(recordPurchase, order.data);
      if (action.successFunction) {
        action.successFunction(order.data);
      }
    }
  } catch (error) {
    if (action.errorFunction) {
      action.errorFunction();
    }
    yield call(handleError, error, 'updateAndCharge');
  }
}

export function* updateAndCharge(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    const { address, tender } = action;

    if (address) {
      const cart = {
        billTo: address
      };
      if (action.isSaveAddress) {
        cart.billTo = yield call(saveAddress, address);
      }
      yield call(updateCart, cartId(), cart);
    }
    if (action.isSaveCreditCard && tender) {
      tender.creditCard.recaptchaToken = action.newCreditCardToken;
      tender.creditCard = yield call(saveCreditCard, tender.creditCard);
    }
    if (tender) {
      yield call(replaceTender, cartId(), tender);
    }
    const finalCart = yield call(chargeCart, cartId(), {
      recaptchaToken: action.token
    });
    yield put({ type: GET_CART_SUCCESS, payload: finalCart.data });
    Cookies.remove('cartId');
    let response;
    if (Cookies.get('customerToken')) {
      response = yield call(getOrder, customerId(), finalCart.data.orderId);
    } else {
      response = yield call(
        getOrderByEmailOrderNumber,
        finalCart.data.email,
        finalCart.data.orderNumber
      );
    }
    yield put({ type: GET_ORDER_SUCCESS, payload: response.data });
    yield put({ type: CHARGE_CART_SUCCESS_GTM, payload: response.data });
    yield call(recordPurchase, response.data);
    if (action.successFunction) {
      action.successFunction(response.data);
    }
  } catch (error) {
    if (action.errorFunction) {
      action.errorFunction();
    }
    yield call(handleError, error, 'updateAndCharge');
  }
}

export function* completeCart(cart) {
  yield put({ type: GET_CART_SUCCESS, payload: cart.data });
  Cookies.remove('cartId');
  let response;
  if (Cookies.get('customerToken')) {
    response = yield call(getOrder, customerId(), cart.data.orderId);
  } else {
    response = yield call(
      getOrderByEmailOrderNumber,
      cart.data.email,
      cart.data.orderNumber
    );
  }
  yield put({ type: GET_ORDER_SUCCESS, payload: response.data });
  yield put({ type: CHARGE_CART_SUCCESS_GTM, payload: response.data });
  yield call(recordPurchase, response.data);
  return response;
}

function* saveAddress(address) {
  let newAddressDb;
  const addressToSave = address;
  if (addressToSave.customerAddressId) {
    const id = addressToSave.customerAddressId;
    delete addressToSave.customerAddressId;
    newAddressDb = yield call(editCustomerAddress, id, addressToSave);
  } else {
    delete addressToSave.customerAddressId;
    newAddressDb = yield call(addCustomerAddress, addressToSave);
  }
  const newAddress = {
    birthDate: newAddressDb.data.birthDate,
    firstName: newAddressDb.data.firstName,
    lastName: newAddressDb.data.lastName,
    address: newAddressDb.data.address,
    address2: newAddressDb.data.address2,
    city: newAddressDb.data.city,
    stateCode: newAddressDb.data.stateCode,
    zipCode: newAddressDb.data.zipCode,
    countryCode: newAddressDb.data.countryCode,
    phone: newAddressDb.data.phone,
    customerAddressId: newAddressDb.data.id
  };
  return newAddress;
}

function* saveCreditCard(creditCard) {
  let newCreditCardDb;
  const creditCardToSave = creditCard;
  if (creditCardToSave.customerCreditCardId) {
    const id = creditCardToSave.customerCreditCardId;
    delete creditCardToSave.customerCreditCardId;
    newCreditCardDb = yield call(editCustomerCreditCard, id, creditCardToSave);
  } else {
    delete creditCardToSave.customerCreditCardId;
    newCreditCardDb = yield call(addCustomerCreditCard, creditCardToSave);
  }
  const newCreditCard = {
    customerCreditCardId: newCreditCardDb.data.id
  };
  return newCreditCard;
}

export function* charge(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    const cart = yield call(chargeCart, cartId(), {
      recaptchaToken: action.recaptchaToken
    });
    const response = yield call(completeCart, cart);
    if (action.successFunction) {
      action.successFunction(response.data);
    }
  } catch (error) {
    if (action.errorFunction) {
      action.errorFunction();
    }
    yield call(handleError, error, 'charge');
  }
}

let isCreating = false;

function* create() {
  while (isCreating) {
    yield delay(500);
    yield call(create);
  }
  isCreating = true;
  if (!cartId()) {
    const newCart = {
      channel: 'Web',
      connectionInformation: {
        userAgent: navigator.userAgent
      }
    };
    if (Cookies.get('customerToken')) {
      newCart.customerId = jwtDecode(Cookies.get('customerToken')).sub;
    }
    if (Cookies.get('c7EmailId')) {
      newCart.additionalInfo = {
        emailId: Cookies.get('c7EmailId')
      };
    }
    if (Cookies.get('c7metaData')) {
      newCart.metaData = JSON.parse(Cookies.get('c7metaData'));
    }
    try {
      const cart = yield call(createCart, newCart);
      Cookies.set('cartId', cart.data.id, {
        expires: 365,
        secure: process.env.NODE_ENV === 'production'
      });
    } catch {
      delete newCart.metaData;
      const cart = yield call(createCart, newCart);
      Cookies.set('cartId', cart.data.id, {
        expires: 365,
        secure: process.env.NODE_ENV === 'production'
      });
    }
  }
  isCreating = false;
}

function* itemAdd(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    yield put({ type: SIDE_CART_VISIBLE });
    yield call(create); // Esure we have a cart
    const response = yield call(addItem, cartId(), action.item);
    yield put({ type: GET_CART_SUCCESS, payload: response.data });
    yield put({
      type: ADD_CART_ITEM_SUCCESS_GTM,
      payload: { item: action.item, cart: response.data }
    });
    const event = {
      action: 'Add To Cart',
      payload: {}
    };
    if (action.analytics) {
      event.payload.view = action.analytics.viewType;
    }
    if (action.analytics && action.analytics.viewType !== 'Detail') {
      event.payload.position = action.analytics.position;
      event.payload.totalProducts = action.analytics.totalProducts;
    }
    logEvent(event);
    if (action.successFunction) {
      action.successFunction();
    }
  } catch (error) {
    // yield put({ type: SIDE_CART_NOT_VISIBLE });
    yield put({ type: RESET_CART_ISFETCHING });
    yield call(handleError, error, 'itemAdd');
  }
}

function* itemAddQuick(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    yield call(create); // Esure we have a cart
    const response = yield call(addItem, cartId(), action.item);
    yield put({ type: GET_CART_SUCCESS, payload: response.data });
    yield put({
      type: ADD_CART_ITEM_SUCCESS_GTM,
      payload: { item: action.item, cart: response.data }
    });
    const event = {
      action: 'Add To Cart',
      payload: {}
    };
    if (action.analytics) {
      event.payload.view = action.analytics.viewType;
      event.payload.position = action.analytics.position;
      event.payload.totalProducts = action.analytics.totalProducts;
    }
    logEvent(event);
    if (action.successFunction) {
      action.successFunction();
    }
  } catch (error) {
    action.errorFunction(error);
    yield put({ type: RESET_CART_ISFETCHING });
    yield call(handleError, error, 'itemAddQuick');
  }
}

export function* itemRemove(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    const response = yield call(
      removeItem,
      cartId(),
      action.sku,
      action.useLoyaltyPoints
    );
    yield put({
      type: REMOVE_CART_ITEM_SUCCESS_GTM,
      payload: { cart: response.data, sku: action.sku }
    });
    yield put({ type: GET_CART_SUCCESS, payload: response.data });
  } catch (error) {
    yield put({ type: RESET_CART_ISFETCHING });
    yield call(handleError, error, 'itemRemove');
  }
}

export function* checkoutStep(action) {
  const { cart, step } = action.payload;

  const products = cart.items.map((item) => {
    const product = {
      //  adding a product to a shopping cart.
      name: item.productTitle,
      id: item.sku,
      price: item.price,
      // brand: 'Google',
      // category: 'Apparel',
      variant: item.productVariantTitle,
      quantity: item.quantity
    };
    return product;
  });
  let promosOrCoupons = [...cart.promotions, ...cart.coupons];
  promosOrCoupons = promosOrCoupons.filter((promo) => promo.inUse === true);
  const promotions = promosOrCoupons.map((promo) => {
    const promotion = {
      //  adding a product to a shopping cart.
      title: promo.title,
      code: promo.code,
      value: promo.totalValue
    };
    return promotion;
  });
  const shippingCost = cart.shipping.reduce(
    (total, item) => total + item.price,
    0
  );
  const event = {
    action: 'Checkout Step',
    payload: {
      step,
      shippingCost,
      products,
      promotions
    }
  };
  logEvent(event);
  yield put({
    type: CHECKOUT_STEP_REQUEST_GTM,
    payload: {
      step: action.payload.step
    }
  });
}

export function recordPurchase(order) {
  const products = order.items.map((item) => {
    const product = {
      //  adding a product to a shopping order.
      name: item.productTitle,
      id: item.sku,
      price: item.price,
      // brand: 'Google',
      // category: 'Apparel',
      variant: item.productVariantTitle,
      quantity: item.quantity
    };
    return product;
  });
  let promosOrCoupons = [...order.promotions, ...order.coupons];
  promosOrCoupons = promosOrCoupons.filter((promo) => promo.inUse === true);
  const promotions = promosOrCoupons.map((promo) => {
    const promotion = {
      //  adding a product to a shopping order.
      title: promo.title,
      code: promo.code,
      value: promo.totalValue
    };
    return promotion;
  });
  const shippingCost = order.shipping.reduce(
    (total, item) => total + item.price,
    0
  );
  const event = {
    action: 'Checkout Complete',
    payload: {
      shippingCost,
      products,
      promotions
    }
  };
  logEvent(event);
}

export function* itemUpdate(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    const item = {
      quantity: action.quantity
    };
    if (action.useLoyaltyPoints) {
      item.itemData = {
        useLoyaltyPoints: action.useLoyaltyPoints
      };
    }
    const response = yield call(updateItem, cartId(), action.sku, item);
    yield put({ type: GET_CART_SUCCESS, payload: response.data });
  } catch (error) {
    action.errorFunction(error);
    yield put({ type: RESET_CART_ISFETCHING });
    yield call(handleError, error, 'itemUpdate');
  }
}

export function* couponAdd(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_REQUESTING });
    yield call(create); // Esure we have a cart as we can start with just a coupon
    const response = yield call(addCouponOrGiftCard, cartId(), {
      code: action.promocode
    });
    yield put({ type: GET_CART_SUCCESS, payload: response.data });
    yield put({
      type: ADD_COUPON_OR_GIFTCARD_SUCCESS_GTM,
      payload: { cart: response.data, code: action.promocode }
    });
    if (action.successFunction) {
      action.successFunction();
    }
  } catch (error) {
    if (
      error.response &&
      (error.response.status === 404 || error.response.status === 422)
    ) {
      action.errorFunction(error.response);
    } else {
      yield call(handleError, error, 'couponAdd');
    }
  }
}

function* handleError(error, callerFunction) {
  // If error is Cart is complete we clear the cart and forward to receipt
  const { response } = error;
  if (
    response &&
    response.status === 422 &&
    response.data &&
    response.data.message === 'Cart is completed and cant be processed'
  ) {
    // If we come from the right caller function
    if (
      ['update', 'updateAndSaveShipping', 'updateAndCharge', 'charge'].includes(
        callerFunction
      )
    ) {
      // Get the cart
      const cart = yield call(getCart, cartId());
      // Clear Cookie
      Cookies.remove('cartId');
      // Forward to receipt page
      if (customerId()) {
        window.location.href = `/checkout/receipt/${cart.data.orderId}`;
      } else {
        window.location.href = `/checkout/receipt?orderNumber=${cart.data.orderNumber}&email=${cart.data.email}`;
      }
    } else {
      // Clear Cookie
      Cookies.remove('cartId');
    }
  } else {
    yield call(addAPIError, error);
  }
}

export function* watchList() {
  yield takeLatest(
    UPDATE_AND_CHARGE_CART_PAYGATE_REQUEST,
    updateAndChargePayGate
  );
  yield takeLatest(GET_CART_REQUEST, get);
  yield takeLatest(GET_CART_HOSTED_PAYMENT_REQUEST, getForHostedPayment);
  yield takeLatest(UPDATE_CART_REQUEST, update);
  yield takeLatest(UPDATE_AND_CHARGE_CART_REQUEST, updateAndCharge);
  yield takeLatest(CHARGE_CART_REQUEST, charge);
  yield takeLatest(ADD_CART_ITEM_REQUEST, itemAdd);
  yield takeEvery(ADD_CART_QUICK_ITEM_REQUEST, itemAddQuick);
  yield takeLatest(REMOVE_CART_ITEM_REQUEST, itemRemove);
  yield takeLatest(UPDATE_CART_ITEM_REQUEST, itemUpdate);
  yield takeLatest(CHECKOUT_STEP_REQUEST, checkoutStep);
  yield takeLatest(
    UPDATE_CART_AND_SAVE_SHIPPING_ADDRESS_REQUEST,
    updateAndSaveShipping
  );
  yield takeLatest(ADD_COUPON_OR_GIFTCARD_REQUEST, couponAdd);
  yield takeLatest(SEND_CART_TO_HOSTED_PAYMENT_REQUEST, sendToHostedPayment);
}

export default function* cartSagas() {
  yield all([watchList()]);
}

const cartId = () => Cookies.get('cartId');
const customerId = () => {
  if (!Cookies.get('customerToken')) {
    return undefined;
  }
  return jwtDecode(Cookies.get('customerToken')).sub;
};
